/**
 * CouncilVisualization Component
 *
 * Orbital system visualization showing AI models as orbiting nodes.
 * Replaces static ProcessCards with a dynamic, living system.
 *
 * Design Philosophy:
 * - Central "Verdict" node as the gravitational center
 * - Models orbit in concentric rings with smooth CSS animations
 * - Communicates "We are live, powerful, waiting" without words
 * - Premium, sophisticated feel - not marketing bullet points
 * - Models fetched dynamically from API (synced with MODEL_REGISTRY)
 *
 * Research Phases:
 * - idle: Calm, slow orbits - "Council awaiting inquiry"
 * - researching: Fast orbits, bright nodes - "Council researching"
 * - deliberating: Pulsing center, nodes converging feel - "Council deliberating"
 * - concluded: Gold glow celebration - "Verdict delivered"
 */

import React, { useMemo } from 'react';
import { cn } from '@/lib/utils';
import type { ResearchPhase } from '@/hooks/useResearchSession';
import { useModels } from '@/hooks/useModels';
import { getProviderLogo, PROVIDER_COLORS } from '@/utils/providerUtils';

interface CouncilVisualizationProps {
  className?: string;
  /** Whether a query is active/being processed */
  isActive?: boolean;
  /** Current research phase for enhanced visual feedback */
  phase?: ResearchPhase;
}

interface OrbitModel {
  name: string;
  provider: string;
  orbit: 1 | 2 | 3;
  delay: string;
}

// Fallback models if API is unavailable
const fallbackOrbitingModels: OrbitModel[] = [
  { name: 'GPT-4o', provider: 'openai', orbit: 1, delay: '0s' },
  { name: 'Claude', provider: 'anthropic', orbit: 1, delay: '-5s' },
  { name: 'Gemini', provider: 'google', orbit: 1, delay: '-10s' },
  { name: 'DeepSeek', provider: 'deepseek', orbit: 2, delay: '0s' },
  { name: 'GPT-4.1', provider: 'openai', orbit: 2, delay: '-6s' },
  { name: 'Sonnet', provider: 'anthropic', orbit: 2, delay: '-12s' },
];

/**
 * Convert API models to orbiting node format with calculated positions
 */
function buildOrbitingModels(
  models: Array<{ name: string; provider: string; tier: string }>
): OrbitModel[] {
  if (models.length === 0) return fallbackOrbitingModels;

  const result: OrbitModel[] = [];

  // Distribute models across orbits based on tier
  // Premium -> inner orbit (1), Standard -> middle orbit (2), Budget -> outer orbit (3)
  const tierOrbitMap: Record<string, 1 | 2 | 3> = {
    premium: 1,
    standard: 2,
    budget: 3,
  };

  // Track how many models are in each orbit for delay calculation
  const orbitCounts: Record<number, number> = { 1: 0, 2: 0, 3: 0 };

  // Limit to ~12 models for visualization (4 per orbit max)
  const limitedModels = models.slice(0, 12);

  for (const model of limitedModels) {
    const orbit = tierOrbitMap[model.tier] || 2;

    // Calculate delay based on position in orbit (spread evenly)
    const orbitPeriods = { 1: 15, 2: 24, 3: 32 }; // seconds per orbit
    const positionInOrbit = orbitCounts[orbit];
    const maxPerOrbit = 4;
    const delay = `-${(positionInOrbit / maxPerOrbit) * orbitPeriods[orbit]}s`;

    result.push({
      name: model.name,
      provider: model.provider,
      orbit,
      delay,
    });

    orbitCounts[orbit]++;
  }

  return result;
}

// Orbit sizes - speed varies based on research phase
const orbitSizes = {
  1: { size: 'w-[220px] h-[220px]', duration: '18s', fastDuration: '8s' },
  2: { size: 'w-[360px] h-[360px]', duration: '28s', fastDuration: '12s' },
  3: { size: 'w-[500px] h-[500px]', duration: '38s', fastDuration: '16s' },
};

// Get status text based on phase
function getStatusText(phase?: ResearchPhase, isActive?: boolean): string {
  switch (phase) {
    case 'planning':
      return 'Council planning research';
    case 'researching':
      return 'Council researching';
    case 'deliberating':
      return 'Council deliberating';
    case 'concluded':
      return 'Verdict delivered';
    case 'error':
      return 'Research interrupted';
    default:
      return isActive ? 'Council active' : 'Council awaiting inquiry';
  }
}

// Get indicator color based on phase
function getIndicatorColor(phase?: ResearchPhase, isActive?: boolean): string {
  switch (phase) {
    case 'planning':
    case 'researching':
      return 'bg-blue-500 animate-pulse';
    case 'deliberating':
      return 'bg-amber-500 animate-pulse';
    case 'concluded':
      return 'bg-emerald-500';
    case 'error':
      return 'bg-red-500';
    default:
      return isActive ? 'bg-emerald-500 animate-pulse' : 'bg-verdict-primary/70 animate-pulse';
  }
}

export function CouncilVisualization({
  className,
  isActive = false,
  phase,
}: CouncilVisualizationProps) {
  // Fetch models from API
  const { models } = useModels();

  // Build orbiting models from API data (memoized to avoid recalculation)
  const orbitingModels = useMemo(() => buildOrbitingModels(models), [models]);

  // Determine if we should use fast animations
  const isFastMode = phase === 'researching' || phase === 'deliberating';
  const isDeliberating = phase === 'deliberating';
  const isConcluded = phase === 'concluded';

  return (
    <div className={cn('relative flex items-center justify-center', className)}>
      {/* Container with fixed aspect ratio - much larger for dramatic impact */}
      <div className="relative w-full aspect-square max-w-[600px]">

        {/* Orbital rings - dashed gold-tinted with subtle pulse */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className="absolute w-[220px] h-[220px] rounded-full"
            style={{
              border: '1px dashed rgba(212, 165, 116, 0.12)',
              animation: 'ring-pulse 6s ease-in-out infinite',
              animationDelay: '0s',
            }}
          />
          <div
            className="absolute w-[360px] h-[360px] rounded-full"
            style={{
              border: '1px dashed rgba(212, 165, 116, 0.08)',
              animation: 'ring-pulse 6s ease-in-out infinite',
              animationDelay: '-2s',
            }}
          />
          <div
            className="absolute w-[500px] h-[500px] rounded-full"
            style={{
              border: '1px dashed rgba(212, 165, 116, 0.05)',
              animation: 'ring-pulse 6s ease-in-out infinite',
              animationDelay: '-4s',
            }}
          />
        </div>

        {/* Center glow effect - intensifies during deliberation */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className={cn(
            'absolute w-48 h-48 rounded-full blur-3xl transition-all duration-1000',
            isDeliberating ? 'bg-verdict-primary/20 scale-110' : 'bg-verdict-primary/8'
          )} />
          <div className={cn(
            'absolute w-28 h-28 rounded-full blur-2xl transition-all duration-1000',
            isDeliberating ? 'bg-verdict-primary/25' : 'bg-verdict-primary/12'
          )} />
          <div className={cn(
            'absolute w-16 h-16 rounded-full blur-xl transition-all duration-1000',
            isDeliberating ? 'bg-verdict-primary/35' : 'bg-verdict-primary/18'
          )} />
        </div>

        {/* Central Verdict node - larger */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative">
            {/* Pulse ring - faster during deliberation */}
            <div className={cn(
              'absolute -inset-4 bg-verdict-primary/20 rounded-full opacity-30',
              isDeliberating ? 'animate-ping' : 'animate-ping',
              isConcluded && 'bg-verdict-primary/40 opacity-50'
            )} />
            {/* Core node - larger and brighter when concluded */}
            <div
              className={cn(
                'w-12 h-12 rounded-full flex items-center justify-center z-10 transition-all duration-500',
                isConcluded ? 'bg-verdict-primary scale-110' : 'bg-verdict-primary'
              )}
              style={{
                boxShadow: isConcluded
                  ? '0 0 60px rgba(212,165,116,0.7), 0 0 120px rgba(212,165,116,0.4)'
                  : '0 0 50px rgba(212,165,116,0.5), 0 0 100px rgba(212,165,116,0.25)',
              }}
            >
              <span className="text-sm font-bold text-black tracking-tight">V</span>
            </div>
          </div>
        </div>

        {/* Orbiting model nodes */}
        {orbitingModels.map((model, index) => (
          <OrbitingNode key={`${model.name}-${index}`} model={model} isFastMode={isFastMode} />
        ))}

        {/* Status indicator - changes based on phase */}
        <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 px-5 py-2.5 bg-surface/90 border border-border/50 rounded-full backdrop-blur-sm">
          <div className={cn('w-2.5 h-2.5 rounded-full', getIndicatorColor(phase, isActive))} />
          <span className="text-sm text-text-primary font-medium whitespace-nowrap tracking-wide">
            {getStatusText(phase, isActive)}
          </span>
        </div>
      </div>
    </div>
  );
}

interface OrbitingNodeProps {
  model: OrbitModel;
  /** Whether to use fast animation mode */
  isFastMode?: boolean;
}

function OrbitingNode({ model, isFastMode = false }: OrbitingNodeProps) {
  const providerHex = (PROVIDER_COLORS[model.provider] || PROVIDER_COLORS.openai).hex;
  const { size, duration, fastDuration } = orbitSizes[model.orbit];
  const animDuration = isFastMode ? fastDuration : duration;

  // Orbit 2 rotates counter-clockwise for visual variety
  const spinKeyframe = model.orbit === 2 ? 'spin-reverse' : 'spin';

  // Stagger breathe animation per node
  const delayNum = parseFloat(model.delay) || 0;
  const breatheDelay = `${delayNum * 0.3}s`;

  return (
    <div className="absolute inset-0 flex items-center justify-center">
      {/* Orbit path container - rotates */}
      <div
        className={cn('absolute rounded-full', size)}
        style={{
          animation: `${spinKeyframe} ${animDuration} linear infinite`,
          animationDelay: model.delay,
        }}
      >
        {/* Node positioned at top of orbit */}
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 group">
          {/* Outer glow halo - provider color */}
          <div
            className={cn(
              'absolute rounded-full transition-all duration-500',
              isFastMode ? '-inset-3 blur-lg opacity-50' : '-inset-2.5 blur-md opacity-30'
            )}
            style={{ backgroundColor: providerHex }}
          />
          {/* Node circle - provider-tinted, no white border */}
          <div
            className={cn(
              'relative rounded-full flex items-center justify-center transition-all duration-500',
              isFastMode ? 'w-8 h-8' : 'w-7 h-7'
            )}
            style={{
              backgroundColor: `${providerHex}18`,
              border: `1.5px solid ${providerHex}40`,
              color: providerHex,
              '--glow-color': `${providerHex}30`,
              animation: 'node-breathe 4s ease-in-out infinite',
              animationDelay: breatheDelay,
            } as React.CSSProperties}
          >
            {getProviderLogo(model.provider, isFastMode ? 16 : 14)}
          </div>
          {/* Label on hover - counter-rotate to stay readable */}
          <div
            className="absolute -top-10 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            style={{
              animation: `${spinKeyframe} ${animDuration} linear infinite reverse`,
              animationDelay: model.delay,
            }}
          >
            <span className="text-xs text-text-primary whitespace-nowrap font-medium bg-surface/95 px-2.5 py-1.5 rounded border border-border/50">
              {model.name}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

